import React, { useEffect, useState } from "react"
import {
  useAppNotificationStore,
  useApplicationStore,
  usePaymentStore,
} from "stores"
import { Button, H4, H6, P1, TextButton, WelcomeNote } from "components"
import {
  ArrowLeft,
  ArrowRight,
  CloseCircle,
  Printer,
  TickCircle,
} from "iconsax-react"
import { formatMoney } from "utils"

type ReceiptType = {
  id?: number
  date: string // Date
  amount: number
  description: string
  transactionNumber: string
}

const TableRow: React.FC<{
  index: number
  transaction: TransactionType
  handleVerifyPayment: Function
}> = ({ index, transaction, handleVerifyPayment }) => {
  return (
    <tr
      key={index}
      className={`hover:bg-pryLight1`}
    >
      <td
        align="center"
        className="align-middle text-base font-normal text-accentText py-[22px] px-2.5"
      >
        <span className={`text-base font-normal text-accentText`}>
          {index + 1}
        </span>
      </td>
      <td
        align="center"
        className="align-middle text-base font-normal text-accentText py-[22px] px-2.5"
      >
        <span className={`text-base font-normal text-accentText`}>
          {transaction.transactionRef}
        </span>
      </td>
      <td
        align="center"
        className="align-middle text-base font-normal text-accentText py-[22px] px-2.5"
      >
        <span className={`text-base font-normal text-accentText`}>
          {transaction.paymentDescription}
        </span>
      </td>
      <td
        align="center"
        className="align-middle text-base font-normal text-accentText py-[22px] px-2.5"
      >
        <span className={`text-base font-normal text-accentText`}>
          #{formatMoney(transaction.transactionAmount, 0)}
        </span>
      </td>
      <td
        align="center"
        className="align-middle text-base font-normal text-accentText py-[22px] px-2.5"
      >
        <span className={`text-base font-normal text-accentText`}>
          {transaction.transactionDate}
        </span>
      </td>
      <td
        align="center"
        className="align-middle text-base font-normal text-accentText py-[22px] px-2.5"
      >
        {transaction.isSuccessful ? (
          <TickCircle className="text-stateSuccess" />
        ) : (
          <CloseCircle className="text-stateError" />
        )}
      </td>
      <td
        align="center"
        className="align-middle text-base font-normal text-accentText py-[22px] px-2.5"
      >
        <Button
          type="button"
          title="Verify"
          color="primary"
          onClick={() => handleVerifyPayment(transaction.transactionRef)}
        >
          Verify
        </Button>
      </td>
    </tr>
  )
}
type TransactionType = {
  transactionRef: string
  paymentDescription: string
  paidBy: string
  transactionDate: string
  transactionAmount: number
  isSuccessful: boolean
}
export const PaymentHistory: React.FC<{}> = () => {
  const { setDashboardContext } = useAppNotificationStore()

  const { getPrintRecieptUrl } = useApplicationStore()
  const { getPayments, verifyPayment } = usePaymentStore()
  const [transactions, setTransactions] = useState<TransactionType[]>([])
  const { toast } = useAppNotificationStore()

  useEffect(() => {
    setDashboardContext("Receipts")
  }, [setDashboardContext])

  const handleGetPayments = React.useCallback(async () => {
    try {
      await getPayments().then((resp) => {
        if (resp.isSuccessful === true) {
          setTransactions(resp.data)
        } else {
          toast.error(resp.message)
        }
      })
    } catch (error) {
      console.log(error)
    }
  }, [getPayments, toast])

  const handleVerifyPayment = async (
    ref: TransactionType["transactionRef"]
  ) => {
    try {
      await verifyPayment({ transactionReference: ref }).then((resp) => {
        if (resp.isSuccessful === true) {
          toast.success(resp.message)
        } else {
          toast.error(resp.message)
        }
      })
    } catch (error) {
      console.log(error)
    }
  }
  useEffect(() => {
    handleGetPayments()
  }, [handleGetPayments])

  // let countBegin = 1
  // const [receipts, updateReceipts] = useState<ReceiptType[]>(
  //   new Array<ReceiptType[]>(50)
  //     .fill([
  //       {
  //         date: "06-01-2023",
  //         amount: 25_000,
  //         description: "Acceptance fee",
  //         transactionNumber: "215634678289",
  //       },
  //     ])
  //     .flat()
  //     .map((receipt) => ({ id: countBegin++, ...receipt }))
  // )
  // const [pagination, setPagination] = useState({
  //   current: 1,
  //   total: Math.ceil(receipts?.length! / 10),
  //   limit: 10,
  // })
  // const [slicedReceipts, updateSlicedReceipts] = useState<ReceiptType[]>([])

  return (
    <div className="flex flex-col gap-10">
      <WelcomeNote />
      <div className="flex flex-col gap-7">
        <div className="flex flex-col gap-12 items-center">
          <div className="flex flex-col items-center gap-6">
            <H4 className="font-semibold text-accentText">All Payments</H4>
            <P1 className="text-accentGrey1 text-center">
              All information about your payment history
            </P1>
          </div>
          <div className="w-full px-6 py-4 bg-pryBase">
            <H6 className="text-white font-semibold">
              List of Payment Receipts
            </H6>
          </div>
        </div>

        {/* Table */}
        <div className="w-full overflow-x-auto">
          <table className="h-auto w-full table table-auto">
            <thead className="table-header-group bg-pryLight1 text-accentText">
              <tr>
                <th
                  align="center"
                  className="align-middle px-2.5 font-medium text-base py-4 min-w-[60px]"
                >
                  S/N
                </th>
                <th
                  align="center"
                  className="align-middle px-2.5 font-medium text-base py-4 min-w-[112px]"
                >
                  Transaction&nbsp;Reference
                </th>
                <th
                  align="center"
                  className="align-middle px-2.5 font-medium text-base py-4 min-w-[144px]"
                >
                  Payment&nbsp;Description
                </th>
                <th
                  align="center"
                  className="align-middle px-2.5 font-medium text-base py-4 min-w-[232px]"
                >
                  Transaction&nbsp;Amount
                </th>
                <th
                  align="center"
                  className="align-middle px-2.5 font-medium text-base py-4 min-w-[168px]"
                >
                  Transaction&nbsp;Date
                </th>
                <th
                  align="center"
                  className="align-middle px-2.5 font-medium text-base py-4 min-w-[168px]"
                >
                  Status
                </th>
                <th
                  align="center"
                  className="align-middle px-2.5 font-medium text-base py-4 min-w-[96px]"
                >
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="bg-white">
              {transactions.map((transaction, index) => (
                <>
                  {index === 0 && (
                    <tr key={`${index}__br`}>
                      <td
                        colSpan={7}
                        className="h-2.5"
                      ></td>
                    </tr>
                  )}
                  <TableRow
                    key={index}
                    index={index}
                    transaction={transaction}
                    handleVerifyPayment={handleVerifyPayment}
                  />
                  {transactions.length - 1 > index && (
                    <tr key={`${index}__hr`}>
                      <td colSpan={7}>
                        <hr className="mx-auto my-1 border-accentGrey3 w-full" />
                      </td>
                    </tr>
                  )}
                  {transactions.length - 1 === index && (
                    <tr key={`${index}__br`}>
                      <td
                        colSpan={7}
                        className="h-5"
                      ></td>
                    </tr>
                  )}
                </>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center gap-10 justify-between lg:justify-end">
          {/* Previous */}
          {/* <TextButton
            title="Previous"
            disabled={pagination.current <= 1}
            onClick={() => {
              setPagination({ ...pagination, current: pagination.current - 1 })
            }}
            className="text-pryBase flex gap-2"
          >
            <ArrowLeft className="w-5" />
            <span>Previous</span>
          </TextButton> */}
          {/* Next */}
          {/* <TextButton
            title="Next"
            disabled={pagination.current === pagination.total}
            onClick={() => {
              setPagination({ ...pagination, current: pagination.current + 1 })
            }}
            className="text-pryBase flex gap-2"
          >
            <span>Next</span>
            <ArrowRight className="w-5" />
          </TextButton> */}
        </div>
      </div>
    </div>
  )
}
